import type { SaleResource } from '@repo/api-client';

/**
 * Whether a sale has been refunded.
 *
 * One function rather than the check written out at each site, for the reason
 * `isVoided` is one function: the list, the record and the refund control are
 * three readers of one field, and a nullable timestamp is exactly the shape that
 * grows a `!== undefined` in one place and not in the others.
 *
 * **A refunded sale is found and shown marked, never reported as missing**
 * (FR-447). Somebody disputing a charge that was refunded needs to see that it
 * was refunded — that is the answer to their question — which is why nothing
 * anywhere uses this to filter a sale out.
 */
export const isRefunded = (sale: SaleResource): boolean =>
  sale.refundedAt !== null && sale.refundedAt !== undefined;

/**
 * Whether to offer refunding this sale — which is **not** the same question as
 * whether the API will accept one.
 *
 * The answer here is the half a screen can honestly know: a sale that is already
 * refunded has nothing left to reverse, and offering the control would be
 * offering an act that can only be refused. Pressing it anyway is not something
 * this guards against; it is about not drawing a button whose only outcome is
 * `409 SALES_ALREADY_REFUNDED`.
 *
 * ## The half this cannot answer, and must not pretend to
 *
 * The other cut-off is the sale's **own** business day: a sale from a day that
 * has since closed cannot be refunded even while its outlet has a day open right
 * now, and the API refuses that with `409 SALES_DAY_CLOSED`. **Nothing in the
 * shipped document makes it decidable.** `SaleResource` carries `businessDayId`
 * and nothing else about the day — no date, no open-or-closed — and there is no
 * business-day route in the document at all, so there is nothing to read the
 * state from. It is the same gap `ticket-void.ts` reports for a void, one epic
 * on, and it is reported again in this PR.
 *
 * Two things follow, and both are deliberate:
 *
 * - **Nothing here guesses from a clock.** The obvious substitute — compare
 *   `recordedAt` against today — is wrong in exactly the case the rule exists
 *   for: the cut-off is a *day's* state and not an age, so a sale from last week
 *   may be perfectly refundable and one from this morning may not. Guessing would
 *   hide a control that works and offer one that does not, and it would put a
 *   client-side clock in front of a money decision the server owns.
 * - **The `409` is handled where it lands.** The dialog words `SALES_DAY_CLOSED`
 *   when it arrives, and words it differently from `SALES_ALREADY_REFUNDED`: the
 *   copy for both is in `error-messages.ts`, keyed by code, and neither screen
 *   writes refusal prose of its own.
 *
 * ## Do not add a client-side already-refunded guard to the *terminal* path
 *
 * The server decides already-refunded on the **refund's own id** before anything
 * else: a till that refunded a sale, lost the response and replays the same
 * refund is answered `200` with the original outcome rather than refused. That is
 * what keeps an offline correction from stalling, and a client that pre-empted it
 * would break exactly the case it was written for.
 */
export const isRefundable = (sale: SaleResource): boolean => !isRefunded(sale);
